import React, { useEffect, useState } from 'react';
import { Col, Row, Tooltip, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { findIndex, debounce, trim } from 'lodash';

import Modal from "../../../components/uiComponent/modal/index";
import Input from '../../../components/uiComponent/inputs/labelInput';
import Button from "../../../components/uiComponent/button/index";
import AsyncSearch from '../../../components/uiComponent/asyncSelect/index.js';

import InventoryWrapper from '../../inventory/style.js';

import { MapPoAndSkus, GetPoSkusList, clearField, updateSoftDeleteStatus } from '../../../slices/po';
import { copyToClip } from '../../../utils/utils.js';

import {STORAGE_IMAGE_URL} from '../../../constants/storage.js';

const ProductImg = `${STORAGE_IMAGE_URL}/newImages/nestle.svg`;
const copyIcon = `${STORAGE_IMAGE_URL}/newImages/copy-white-icon.svg`;
const deleteIcon = `${STORAGE_IMAGE_URL}/newImages/delete-outlined.svg`;
const noData = `${STORAGE_IMAGE_URL}/newImages/no-data-icon.svg`;

const AddSkuModal = ({ open, onHide, onSave, productId, poItemId }) => {
  const dispatch = useDispatch();

  const {
    poItems,
    mappingPoAndSkus,
    fetchingPoSkusList,
    poSkusList,
    totalSkusListCount
  } = useSelector((state) => state.pos || {});

  const [addedSkus, setAddedSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(null);
  const [index, setIndex] = useState(findIndex(poItems, ({ product }) => product.id === productId));
  const [page, setPage] = useState({ pageSize: 50, pageNumber: 1 });
  const [filters, setFilters] = useState({ search: '' });

  useEffect(() => {
    dispatch(GetPoSkusList({
      po_item_id: poItemId,
      product_id: productId,
      page,
      filters
    }));

    return () => {
      dispatch(clearField({
        field: 'poSkusList'
      }));
    }
  }, []);

  const handleAddSku = () => {
    if (selectedSku && selectedSku?.bundle_quantity) {
      const newSku = {
        id: selectedSku.value,
        image: selectedSku.image || ProductImg,
        title: selectedSku.label,
        asin: selectedSku.identifier1,
        seller_sku: selectedSku.identifier2,
        qty: selectedSku?.bundle_quantity
      };

      setAddedSkus(prev => {
        const existingItemIndex = prev.findIndex(item => item.seller_sku === newSku.seller_sku);

        if (existingItemIndex !== -1) {
          const updatedSkus = [...prev];
          updatedSkus[existingItemIndex].qty = Number(updatedSkus[existingItemIndex].qty) + Number(newSku.qty);
          return updatedSkus;
        }
        return [newSku, ...prev];
      });
      setSelectedSku(null);
      dispatch(updateSoftDeleteStatus({
        field: 'poSkusList',
        id: selectedSku.value,
        value: true
      }));
    }
  };

  const handleDeleteSku = (index) => {
    const id = addedSkus[index]?.id;

    setAddedSkus((prev) => prev.filter((_, i) => i !== index));
    dispatch(updateSoftDeleteStatus({
      field: 'poSkusList',
      id,
      value: false
    }));
  };

  const getMappedSkusList = (poSkusList) => {
    return poSkusList?.map(({ id: value, image_url: image, item_name: label, sku, vendorasin, bundle_quantity }) => ({
      value,
      image,
      label,
      identifier1: vendorasin?.asin,
      identifier2: sku,
      bundle_quantity
    })) || [];
  };


  const handleSkuSearch = debounce((value, callback) => {
    if (trim(value) || filters.search) {
      setFilters({ search: trim(value) });
      setPage(page => ({ ...page, pageNumber: 1 }));
      const deletedIds = poSkusList?.filter(item => item.deleted)?.map(item => item.id) || [];

      dispatch(GetPoSkusList({
        po_item_id: poItemId,
        product_id: productId,
        page: { ...page, pageNumber: 1 },
        filters: { search: trim(value) },
      }))
        .then(({ payload }) => {
          const { data } = payload?.data || {};

          const updatedData = data?.map(item => ({
            ...item,
            deleted: deletedIds.includes(item.id) ? true : item.deleted
          }));

          callback?.(getMappedSkusList(updatedData?.filter(({ deleted }) => !deleted)));
        });
    }
  }, 800);

  return (
    <Modal
      open={open}
      onHide={onHide}
      size="meduim"
      closeText="Cancel"
      saveText="Confirm"
      header={true}
      closeButton={true}
      className="modal-sku-product-wrapper"
      onClose={onHide}
      onSave={() => {
        const mappedSkus = addedSkus?.map(({ id, qty }) => ({ id, qty }));
        dispatch(MapPoAndSkus({
          mappedSkus,
          po_item_id: poItemId,
        })).then(({ payload }) => {
          if (payload?.success) {
            onSave(poItems[index]?.id);
            onHide();
          }
        });
      }}
      buttonDisabled={mappingPoAndSkus || !addedSkus?.length}
    >
      <InventoryWrapper className='sku-modal'>
        <Spin tip='Loading...' spinning={mappingPoAndSkus}>
          <h4 className='text-primary fw-bold mb-3'>MAP SKU to PO Item</h4>
          <div className="product-title mb-4">
            <img src={poItems[index]?.product?.image_url || ProductImg} alt="product" width={28} height={28} className='product-img' />
            <div className='w-100'>
              <Tooltip placement="bottom" title={poItems[index]?.product?.name}>
                <span className="product-name">{poItems[index]?.product?.name}</span>
              </Tooltip>
              <div className='asin'>
                <span>{poItems[index]?.product?.identifier_value}</span>
                <img src={copyIcon} alt='copy' className='cursor-pointer'
                  onClick={() => copyToClip(poItems[index]?.product?.identifier_value)}
                />
              </div>
            </div>
          </div>
          <Row gutter={24} className='modal-inner-wrapper-sku mb-5'>
            <Col span={10}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label
                  style={{
                    marginBottom: 4,
                    color: '#78909C',
                    fontSize: 12
                  }}
                >
                  Search SKUs
                </label>
                <AsyncSearch
                  placeholder="SKU"
                  isClearable={true}
                  isSearchable={true}
                  options={getMappedSkusList(poSkusList?.filter(({ deleted }) => !deleted))}
                  value={selectedSku}
                  // loadMoreData={handleLoadMoreSkus}
                  loading={fetchingPoSkusList}
                  onChange={(e) => setSelectedSku(e)}
                  handleSearch={handleSkuSearch}
                />
              </div>
            </Col>
            <Col span={14}>
              <div
                className="d-flex align-items-end gap-3 add-sku-btn new-sku-button"
              >
                <Input
                  placeholder="Enter Quantity"
                  value={selectedSku?.bundle_quantity || 0}
                  type="number"
                  label="Pack Quantity"
                  disabled={true}
                  width="90%"
                  marginBottom="0"
                />
                <Button
                  text="Add SKU"
                  className="mb-0"
                  onClick={handleAddSku}
                  disabled={!selectedSku || !selectedSku?.bundle_quantity || selectedSku?.bundle_quantity <= 0}
                />
              </div>
            </Col>
          </Row>
          {addedSkus.length === 0 ?
            <div className='no-data d-flex flex-column align-items-center justify-content-center'>
              <img src={noData} alt='no-data' />
              <h6>No Data</h6>
            </div>
            :
            <div className='map-height'>
              {addedSkus?.map((sku, index) => (
                <div className="product-title option-product card-item-wrapper" key={index}>
                  <img src={sku.image} alt="product" width={28} height={28} className="product-img" />
                  <div className="d-flex align-items-center justify-content-between gap-3 card-overlay-opulent">
                    <div className="card-item-content">
                      <Tooltip placement="bottom" title={sku.title}>
                        <span className="product-name">{sku.title}</span>
                      </Tooltip>
                      <div className="d-flex gap-2">
                        <div className="asin">
                          <span>ASIN <span>{sku.asin}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(sku.asin)} />
                        </div>
                        <div className="asin sku">
                          <span>SKU <span>{sku.seller_sku}</span></span>
                          <img src={copyIcon} alt="copy" className="cursor-pointer"
                            onClick={() => copyToClip(sku.seller_sku)} />
                        </div>
                      </div>
                    </div>
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="cursor-pointer"
                      onClick={() => handleDeleteSku(index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          }
        </Spin>
      </InventoryWrapper>
    </Modal>
  );
};

export default AddSkuModal;
